import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Cloud, CheckCircle, Download, Calendar, DollarSign, Users, ArrowRight } from 'lucide-react';

const CloudDevOpsPage = () => {
  const weeks = [
    {
      week: 'Week 0',
      title: 'Mini Foundations',
      topics: ['Linux basics', 'Linux puzzles', 'Git & GitHub basics', 'Networking basics', 'Cloud models (IaaS/PaaS/SaaS)'],
      outcome: 'Ready for cloud + DevOps work',
      time: '1.5-2 hrs/day'
    },
    {
      week: 'Week 1',
      title: 'Cloud Foundations',
      topics: ['Pick primary cloud (AWS/Azure/GCP)', 'Explore console', 'Deploy a static website', 'Check usage & cost calculation'],
      outcome: 'Hands-on with cloud console',
      time: '1.5-2 hrs/day'
    },
    {
      week: 'Week 2',
      title: 'Linux Servers & Networking',
      topics: ['Launch a VM', 'Understand networking layers', 'SSH into VM', 'Install Nginx & serve page', 'Read logs'],
      outcome: 'Operate real servers',
      time: '1.5-2 hrs/day'
    },
    {
      week: 'Week 3',
      title: 'Terraform Infrastructure as Code (Part 1)',
      topics: ['Terraform basics', 'First configuration', 'Variables & outputs', 'State & remote state', 'Clean up resources'],
      outcome: 'Deploy infra using IaC',
      time: '1.5-2 hrs/day'
    },
    {
      week: 'Week 4',
      title: 'Terraform Advanced + Multi-Cloud (Part 2)',
      topics: ['Terraform modules', 'Workspaces', 'Providers', 'Multi-cloud IaC best practices'],
      outcome: 'Multi-cloud-ready IaC engineer',
      time: '1.5-2 hrs/day'
    },
    {
      week: 'Week 5',
      title: 'Containers (Docker Essentials)',
      topics: ['Docker basics', 'Containerize simple app', 'Dockerfile optimisation', 'Private container registry', 'Docker Compose'],
      outcome: 'Able to run containerized workloads',
      time: '2 hrs/day'
    },
    {
      week: 'Week 6',
      title: 'Kubernetes Foundations (Local + Cloud)',
      topics: ['Install Minikube / K3s', 'Pods, Deployments, Services', 'ConfigMaps, Secrets', 'Container network fundamentals', 'Deploy your Docker app to K8s'],
      outcome: 'Beginner K8s engineer',
      time: '2 hrs/day'
    },
    {
      week: 'Week 7',
      title: 'CI/CD Pipelines (Automation)',
      topics: ['CI/CD concepts', 'GitHub Actions', 'Add Docker build', 'Jenkins basics (optional)', 'Deploy to K8s or VM via CI'],
      outcome: 'DevOps pipeline automation',
      time: '2 hrs/day',
      projects: ['Two-tier Flask App', 'Three-tier architecture demo']
    },
    {
      week: 'Week 8',
      title: 'Cloud Security (IAM & Secrets)',
      topics: ['IAM fundamentals', 'Limited IAM roles/users', 'Secrets management', 'KMS basics', 'Enable CloudTrail & logs', 'Cloud security patterns'],
      outcome: 'Security-first DevOps mindset',
      time: '1.5 hrs/day'
    },
    {
      week: 'Week 9',
      title: 'Monitoring, Logging & AIOps',
      topics: ['Metrics + Prometheus', 'Grafana dashboards', 'Monitor Docker containers', 'CloudWatch or Azure Monitor', 'Intro to AIOps'],
      outcome: 'You can operate real environments',
      time: '1.5-2 hrs/day'
    },
    {
      week: 'Week 10',
      title: 'Cloud Architecture & Scaling',
      topics: ['Load balancers', 'Auto scaling', 'VPC design', 'Subnets, routing, gateways', 'Multi-tier architectures'],
      outcome: 'Cloud architecture understanding',
      time: '1.5-2 hrs/day'
    },
    {
      week: 'Week 11',
      title: 'DevOps Real-World Scenario Projects',
      topics: ['IaC + Docker + K8s cloud deploy', 'Monitoring + logging integration', 'IAM + security pipeline'],
      outcome: 'Portfolio-ready project',
      time: '2 hrs/day'
    },
    {
      week: 'Week 12',
      title: 'Portfolio, Resume, Mock Interviews',
      topics: ['Build your full DevOps portfolio', 'Write case studies', 'LinkedIn optimisation', 'GitHub refinement', 'Mock interviews'],
      outcome: 'Ready for your first DevOps role',
      time: '2 hrs/day'
    }
  ];

  const skills = [
    'Cloud Foundations (AWS/Azure/GCP)',
    'Linux for DevOps',
    'Terraform IaC',
    'Docker',
    'Kubernetes',
    'CI/CD',
    'Monitoring & Logging',
    'IAM & Security',
    'AIOps Fundamentals',
    'Cloud Architecture'
  ];

  const tools = [
    'AWS / Azure / GCP',
    'Terraform',
    'Docker',
    'Kubernetes / Minikube',
    'GitHub Actions / Jenkins',
    'Prometheus / Grafana',
    'Linux / SSH',
    'CloudWatch or Azure Monitor'
  ];

  const careers = [
    'DevOps Engineer',
    'Cloud Engineer',
    'Platform Engineer',
    'Site Reliability Engineer (SRE)',
    'Automation Engineer',
    'Junior DevOps'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0B1120 100%)' }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#2563EB] text-white">12 Weeks</span>
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#16A34A] text-white">Beginner Friendly</span>
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#6366F1] text-white">Live Mentors</span>
              </div>
              
              <h1 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                Cloud & DevOps Engineer Program
              </h1>
              
              <p className="text-xl text-[#C7D2FE] mb-8">
                Learn Cloud, DevOps, CI/CD, Kubernetes, Terraform, Security & AIOps - Become Job Ready
              </p>
              
              <div className="flex items-center gap-4 mb-8 text-white">
                <div className="flex items-center gap-2">
                  <DollarSign size={20} />
                  <span className="font-semibold">$80,000 - $130,000</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={20} />
                  <span>1.5-2 hrs/day</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold transition-all">
                  Enroll Now
                  <ArrowRight size={20} />
                </Link>
                <button className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-semibold transition-all">
                  <Download size={20} />
                  Download Course PDF
                </button>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-[24px] p-8 border border-white/10 backdrop-blur-md" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)' }}>
                <Cloud size={200} className="text-[#2563EB] mx-auto mb-4" strokeWidth={1} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="py-20 px-4 bg-[#F9FAFB]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              Program Overview
            </h2>
            <p className="text-lg text-[#4B5563] max-w-4xl mx-auto leading-relaxed">
              Become a Cloud & DevOps Engineer in 12 weeks through a fully structured, project-first curriculum. Start from zero and build expertise in cloud computing, Linux, IaC with Terraform, containers, Kubernetes, CI/CD pipelines, security, IAM, and AIOps. Graduate with a complete portfolio and confidence to apply for real DevOps roles.
            </p>
          </div>
        </div>
      </section>

      {/* 12-Week Curriculum */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-12 text-center" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            12-Week Curriculum
          </h2>
          
          <div className="space-y-6">
            {weeks.map((week, idx) => (
              <div key={idx} className="rounded-[20px] p-6 bg-white border border-[#E5E7EB] hover:shadow-lg transition-all">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-24 text-center">
                    <div className="inline-flex px-4 py-2 rounded-full bg-[#2563EB] text-white font-bold text-sm">
                      {week.week}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#0F172A] mb-3">{week.title}</h3>
                    <ul className="space-y-2 mb-4">
                      {week.topics.map((topic, i) => (
                        <li key={i} className="flex items-start gap-2 text-[#4B5563]">
                          <CheckCircle size={16} className="text-[#2563EB] flex-shrink-0 mt-1" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                    {week.projects && (
                      <div className="mb-3 p-3 rounded-xl bg-[#EFF6FF]">
                        <p className="text-sm font-semibold text-[#2563EB] mb-1">Projects:</p>
                        {week.projects.map((project, i) => (
                          <p key={i} className="text-sm text-[#0F172A]">- {project}</p>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-[#6B7280]"><strong>Outcome:</strong> {week.outcome}</span>
                      <span className="text-[#2563EB] font-semibold">{week.time}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills & Tools */}
      <section className="py-20 px-4 bg-[#F9FAFB]">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                Skills You'll Master
              </h2>
              <div className="space-y-3">
                {skills.map((skill, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#E5E7EB]">
                    <CheckCircle size={20} className="text-[#16A34A]" />
                    <span className="text-[#0F172A] font-medium">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                Tools & Technologies
              </h2>
              <div className="space-y-3">
                {tools.map((tool, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#E5E7EB]">
                    <Cloud size={20} className="text-[#2563EB]" />
                    <span className="text-[#0F172A] font-medium">{tool}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-8 text-center" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Career Opportunities
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {careers.map((career, idx) => (
              <div key={idx} className="p-6 rounded-[20px] bg-gradient-to-br from-[#EFF6FF] to-white border border-[#E5E7EB] text-center">
                <Users size={40} className="text-[#2563EB] mx-auto mb-3" />
                <h3 className="text-lg font-bold text-[#0F172A]">{career}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prerequisites */}
      <section className="py-20 px-4 bg-[#F9FAFB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-8" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Prerequisites
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 rounded-[20px] bg-white border border-[#E5E7EB]">
              <CheckCircle size={40} className="text-[#16A34A] mx-auto mb-3" />
              <p className="text-[#0F172A] font-semibold">No prior IT experience required</p>
            </div>
            <div className="p-6 rounded-[20px] bg-white border border-[#E5E7EB]">
              <CheckCircle size={40} className="text-[#16A34A] mx-auto mb-3" />
              <p className="text-[#0F172A] font-semibold">Basic computer literacy</p>
            </div>
            <div className="p-6 rounded-[20px] bg-white border border-[#E5E7EB]">
              <CheckCircle size={40} className="text-[#16A34A] mx-auto mb-3" />
              <p className="text-[#0F172A] font-semibold">Commitment to 1.5-2 hours per day</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(135deg, #020617 0%, #0B1120 100%)' }}>
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Ready to Launch Your DevOps Career?
          </h2>
          <p className="text-xl text-[#C7D2FE] mb-8">
            Join our next cohort starting December 7, 2024
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold transition-all">
              Schedule Free Consultation
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-semibold transition-all">
              Enroll Now
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CloudDevOpsPage;
